import { useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLoader } from '@/hooks/use-loader';
import { DISPLAY_NAME_MAX, getMyProfile, setMyName } from '@/lib/me-api';
import { useAuthStore } from '@/stores/authStore';
import { HandleCell } from '@/components/team/HandleEditButton';
import { DetailRow } from '@/components/DetailRow';
import { MemberAvatar } from '@/components/team/MemberAvatar';
import { useMemberPhotoActions } from '@/components/team/use-member-photo-actions';
import { FormField } from '@/components/FormField';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** Your own name, handle and photo (the account menu's Profile). */
export function MemberProfileDialog({ open, onOpenChange }: Props) {
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const handle = useAuthStore((s) => s.user?.handle ?? null);
  const { data: res, loading, reload } = useLoader(() => getMyProfile(), [userId], {
    enabled: open && !!userId,
  });
  const profile = res?.data?.profile ?? null;
  const { busy, choose, remove, photoUi } = useMemberPhotoActions(reload);

  const [draft, setDraft] = useState<{ forId: string; name: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const savedName = profile?.displayname ?? '';
  const name = draft?.forId === userId ? draft.name : savedName;
  const trimmed = name.replace(/\s+/g, ' ').trim();
  const dirty = trimmed !== savedName;
  const shownName = savedName || profile?.email.split('@')[0] || '';

  const close = (next: boolean) => {
    if (!next) setDraft(null);
    onOpenChange(next);
  };

  const saveName = async () => {
    setSaving(true);
    const saved = await setMyName(trimmed);
    setSaving(false);
    if (saved.error || !saved.data) {
      toast.error(saved.error ?? 'The name was not saved');
      return;
    }
    setDraft(null);
    reload();
    toast.success('Name saved');
  };

  const loadError = res?.error ?? null;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Profile</DialogTitle>
        </DialogHeader>
        {loadError ? (
          <p className="text-sm text-destructive">{loadError}</p>
        ) : (
          <>
          {/* Outside the form below: the handle's own dialog submits its own form. */}
          <dl>
            <DetailRow label="Handle" value={<HandleCell handle={handle} />} />
          </dl>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (dirty && trimmed) void saveName();
            }}
          >
            <div className="flex flex-col items-center gap-3 py-1 text-center">
              <MemberAvatar
                name={shownName || null}
                photourl={profile?.photourl ?? null}
                size="2xl"
                hint={false}
                busy={busy || (loading && !profile)}
              />
              <span className="min-h-5 text-sm text-muted-foreground">{profile?.email}</span>
              <div className="flex flex-wrap justify-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={choose} disabled={busy || !profile}>
                  <Camera />
                  {profile?.photourl ? 'Change photo' : 'Upload photo'}
                </Button>
                {profile?.has_uploaded_photo ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => void remove()} disabled={busy}>
                    <Trash2 />
                    Remove uploaded photo
                  </Button>
                ) : null}
              </div>
              {photoUi}
            </div>
            <FormField
              id="member-display-name"
              label="Name"
              info="Shown across the factory, for example on tasks. Once set here, signing in with Google no longer changes it."
            >
              <Input
                id="member-display-name"
                value={name}
                maxLength={DISPLAY_NAME_MAX}
                disabled={!profile}
                onChange={(event) => setDraft({ forId: userId, name: event.target.value })}
              />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)} disabled={saving}>
                {dirty ? 'Cancel' : 'Done'}
              </Button>
              {dirty ? (
                <Button loading={saving} loadingText="Saving…" type="submit" disabled={!trimmed}>
                  Save name
                </Button>
              ) : null}
            </DialogFooter>
          </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
